import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import CardSurface from '../../components/card-surface/CardSurface'
import Button from '../../components/button/Button'
import { useAuth } from '../../_hooks/use-auth'
import { useResidentTenancy } from '../../_hooks/use-resident-tenancy'
import { claimTenancy } from '../../_data/resident'
import {
  clearPendingClaimPath,
  setPendingClaimPath,
} from '../../_data/pending-claim'
import { PageRoot, CardWrap, Content, Title, Body } from './styled'

type ClaimState = 'loading' | 'signed-out' | 'claiming' | 'success' | 'error'

/**
 * Renter claim landing for `#/claim-tenancy?token=…` (the link emailed when a
 * PM invites a tenant). When signed in it auto-claims; when signed out it
 * stashes the path and routes to sign-in (the public-only guard returns the
 * renter here afterwards, skipping the apartment-search onboarding).
 *
 * @returns {JSX.Element}
 */
const ClaimTenancyPage = (): JSX.Element => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const { refreshTenancies } = useResidentTenancy()

  const token = useMemo<string>(
    () => searchParams.get('token') ?? '',
    [searchParams],
  )

  const [claimState, setClaimState] = useState<ClaimState>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [hasAttempted, setHasAttempted] = useState<boolean>(false)

  useEffect(() => {
    clearPendingClaimPath()
  }, [])

  const runClaim = useCallback(async () => {
    setClaimState('claiming')
    try {
      await claimTenancy(token)
      await refreshTenancies()
      setClaimState('success')
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'We could not claim this tenancy.',
      )
      setClaimState('error')
    }
  }, [token, refreshTenancies])

  useEffect(() => {
    if (isAuthLoading === true) {
      return
    }
    if (token === '') {
      setErrorMessage('This claim link is missing its token.')
      setClaimState('error')
      return
    }
    if (isAuthenticated === false) {
      setClaimState('signed-out')
      return
    }
    if (hasAttempted === false) {
      setHasAttempted(true)
      void runClaim()
    }
  }, [isAuthLoading, isAuthenticated, token, hasAttempted, runClaim])

  const handleGoToSignIn = useCallback(() => {
    setPendingClaimPath(`/claim-tenancy?token=${encodeURIComponent(token)}`)
    navigate('/sign-in')
  }, [navigate, token])

  const handleGoToHome = useCallback(() => {
    navigate('/my-home')
  }, [navigate])

  const isBusy = claimState === 'loading' || claimState === 'claiming'

  return (
    <PageRoot>
      <CardWrap>
        <CardSurface padding="loose">
          <Content>
            {isBusy ? (
              <>
                <Title>Claiming your tenancy…</Title>
                <Body>Hang tight for just a moment.</Body>
              </>
            ) : null}
            {claimState === 'signed-out' ? (
              <>
                <Title>Claim your tenancy</Title>
                <Body>
                  Sign in or create your QuickPass account to connect this
                  tenancy to you.
                </Body>
                <Button variant="primary" onClick={handleGoToSignIn}>
                  Sign in to claim
                </Button>
              </>
            ) : null}
            {claimState === 'success' ? (
              <>
                <Title>You&rsquo;re all set! 🎉</Title>
                <Body>
                  Your tenancy is now connected to your QuickPass — view your
                  balance and details any time.
                </Body>
                <Button variant="primary" onClick={handleGoToHome}>
                  Go to my home
                </Button>
              </>
            ) : null}
            {claimState === 'error' ? (
              <>
                <Title>We hit a snag</Title>
                <Body>{errorMessage}</Body>
                <Button variant="secondary" onClick={handleGoToHome}>
                  Go to my home
                </Button>
              </>
            ) : null}
          </Content>
        </CardSurface>
      </CardWrap>
    </PageRoot>
  )
}

export default ClaimTenancyPage
